import type { DeviceSummary, CreatedDeviceResponse } from "@/features/devices/hooks";
import type { CardSummary } from "@/features/cards/hooks";
import type { SensorSummary } from "@/features/sensors/hooks";
import type { Selection, SensorsByCardId } from "@/types/device-manager.types";
import { ClpDetailPane } from "./ClpDetailPane";
import { CardDetailPane } from "./CardDetailPane";
import { SensorDetailPane } from "./SensorDetailPane";
import { NewClpForm } from "./NewClpForm";

interface DetailPanelProps {
  creatingClp: boolean;
  onCreatedClp: (r: CreatedDeviceResponse) => void;
  onCancelCreate: () => void;
  devicesCount: number;
  sel: Selection;
  clp?: DeviceSummary;
  card?: CardSummary;
  sensor?: SensorSummary;
  cards: CardSummary[];
  sensorsByCardId: SensorsByCardId;
  onSelect: (s: Selection) => void;
  onDirtyChange: (dirty: boolean) => void;
  onDeleted: (next: Selection) => void;
}

function EmptyDetail({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
      <div className="text-[15px] font-medium text-text-primary">{title}</div>
      <div className="max-w-sm text-[13px] text-text-secondary">{description}</div>
    </div>
  );
}

export function DetailPanel(props: DetailPanelProps) {
  const { creatingClp, sel, clp, card, sensor, cards, sensorsByCardId, onSelect } = props;

  if (creatingClp) {
    return <NewClpForm onCreated={props.onCreatedClp} onCancel={props.onCancelCreate} />;
  }
  if (!sel || !clp) {
    return (
      <EmptyDetail
        title={props.devicesCount === 0 ? "Nenhum CLP cadastrado" : "Selecione um item"}
        description={
          props.devicesCount === 0
            ? "Cadastre o primeiro controlador para começar."
            : "Escolha um CLP, cartão ou sensor na árvore à esquerda."
        }
      />
    );
  }

  if (sel.type === "clp") {
    return (
      <ClpDetailPane
        key={clp.id}
        device={clp}
        cards={cards}
        sensorsByCardId={sensorsByCardId}
        onSelectCard={(cardId) => onSelect({ type: "card", clpId: clp.id, cardId })}
        onDirtyChange={props.onDirtyChange}
        onDeleted={() => props.onDeleted(null)}
      />
    );
  }
  if (sel.type === "card") {
    return card ? (
      <CardDetailPane
        key={card.id}
        device={clp}
        card={card}
        sensors={sensorsByCardId.get(card.id) ?? []}
        onSelectClp={() => onSelect({ type: "clp", clpId: clp.id })}
        onSelectSensor={(sensorId) =>
          onSelect({ type: "sensor", clpId: clp.id, cardId: card.id, sensorId })
        }
        onDirtyChange={props.onDirtyChange}
        onDeleted={() => props.onDeleted({ type: "clp", clpId: clp.id })}
      />
    ) : (
      <EmptyDetail title="Carregando…" description="Buscando o cartão selecionado." />
    );
  }
  return card && sensor ? (
    <SensorDetailPane
      key={sensor.id}
      device={clp}
      card={card}
      sensor={sensor}
      onSelectClp={() => onSelect({ type: "clp", clpId: clp.id })}
      onSelectCard={() => onSelect({ type: "card", clpId: clp.id, cardId: card.id })}
      onDirtyChange={props.onDirtyChange}
      onDeleted={() => props.onDeleted({ type: "card", clpId: clp.id, cardId: card.id })}
    />
  ) : (
    <EmptyDetail title="Carregando…" description="Buscando o sensor selecionado." />
  );
}
